import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js'
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, updateProfile, type User as FirebaseUser } from 'firebase/auth'
import type { AppUser } from '../types'
import { firebaseAuth } from './firebase'
import { supabase } from './supabase'

const AVATARS = ['🧠', '🚀', '⚡', '🎯', '🦉']

export function mapAuthUser(user: User): AppUser {
  const metadata = user.user_metadata ?? {}
  const role = user.app_metadata?.role
  return {
    uid: user.id,
    displayName: metadata.full_name || metadata.name || user.email?.split('@')[0] || 'Học sinh',
    nickname: metadata.nickname || metadata.full_name || metadata.name || user.email?.split('@')[0] || 'Học sinh',
    avatar: metadata.avatar || AVATARS[0],
    email: user.email || '',
    provider: 'supabase',
    role: role === 'admin' || role === 'teacher' || role === 'parent' ? role : 'student',
  }
}

export function mapFirebaseUser(user: FirebaseUser): AppUser {
  return {
    uid: user.uid,
    displayName: user.displayName || user.email?.split('@')[0] || 'Học sinh',
    nickname: user.displayName || user.email?.split('@')[0] || 'Học sinh',
    // Google profile URLs are not suitable for the app's emoji avatar picker.
    avatar: user.photoURL && !/^https?:\/\//.test(user.photoURL) ? user.photoURL : AVATARS[0],
    email: user.email || '',
    provider: 'firebase',
    role: 'student',
  }
}

export async function getCurrentUser() {
  if (firebaseAuth.currentUser) return mapFirebaseUser(firebaseAuth.currentUser)
  if (!supabase) return null
  const { data, error } = await supabase.auth.getUser()
  if (error || !data.user) return null
  return mapAuthUser(data.user)
}

export function onAuthChange(callback: (user: AppUser | null) => void) {
  if (firebaseAuth) return onAuthStateChanged(firebaseAuth, user => callback(user ? mapFirebaseUser(user) : null))
  if (!supabase) return () => undefined
  const { data } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
    callback(session?.user ? mapAuthUser(session.user) : null)
  })
  return () => data.subscription.unsubscribe()
}

export async function signInWithGoogle() {
  if (firebaseAuth) {
    await signInWithPopup(firebaseAuth, new GoogleAuthProvider())
    return
  }
  if (!supabase) throw new Error('Supabase chưa được cấu hình.')
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}${window.location.pathname}` },
  })
  if (error) throw error
}

export async function signOutCloud() {
  if (firebaseAuth.currentUser) {
    await signOut(firebaseAuth)
    return
  }
  if (!supabase) return
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function updateCloudProfile(profile: Pick<AppUser, 'nickname' | 'avatar'>) {
  if (firebaseAuth.currentUser) {
    await updateProfile(firebaseAuth.currentUser, { displayName: profile.nickname, photoURL: profile.avatar })
    return
  }
  if (!supabase) return
  const { error } = await supabase.auth.updateUser({ data: { nickname: profile.nickname, avatar: profile.avatar } })
  if (error) throw error
}
